// Standard Angular DI decorator — providedIn:'root' below makes this service
// a singleton without needing to list it in any module's `providers`.
import { Injectable } from '@angular/core';
// Router types we plug into. RouteReuseStrategy is the interface Angular
// calls into on every navigation to decide whether to keep components alive.
import {
    ActivatedRouteSnapshot,
    DetachedRouteHandle,
    RouteReuseStrategy,
} from '@angular/router';
// Shared bits used across policies — the cache-entry shape, the policy
// interface, the URL/key utilities, and the scroll-capture helper.
import {
    captureScrollSnapshot,
    CacheEntry,
    getRouteKey,
    parseQueryParams,
    ReusePolicy,
    ScrollSnapshot,
    stripQuery,
} from './route-reuse/helpers';
// The concrete eviction rules. To add a new one, create another file
// under ./route-reuse/policies and import it here, then register it in
// POLICY_BY_NAME below.
import { preserveAlwaysPolicy } from './route-reuse/policies/preserve-always.policy';
import { smartPolicy } from './route-reuse/policies/smart.policy';
import { strictPolicy } from './route-reuse/policies/strict.policy';

// Single prefix so it's easy to filter/grep in the devtools console.
const LOG = '[RR]';

/**
 * CustomRouteReuseStrategy
 * ------------------------
 * Angular calls our methods on every navigation to ask:
 *   - shouldDetach(route)?  → "leaving this route, want me to keep it alive?"
 *   - store(route, handle)  → "here's the kept-alive component, save it"
 *   - shouldAttach(route)?  → "entering this route, do you have one cached?"
 *   - retrieve(route)       → "give me the cached one to reattach"
 *   - shouldReuseRoute(...) → "is this 'the same route'? (config compare)"
 *
 * A route opts in via `data: { reuseRoute: ... }`. The flag picks one of the
 * eviction policies in ./route-reuse/policies — each policy decides when a
 * cached entry should be thrown away on back-navigation:
 *
 *   - `true` (or `'preserveAlways'`)  default: cache survives every nav.
 *                                     Only `clearFor()` (sidebar click) and
 *                                     `clear()` (logout) ever evict it.
 *   - `'strict'`                       evict on any URL string difference.
 *   - `'smart'`                        evict only if cached query-param
 *                                     values changed (extras/reorder OK).
 *
 * Add a new policy by creating ./route-reuse/policies/<name>.policy.ts and
 * registering it in `POLICY_BY_NAME` below.
 *
 * Wire-up:
 *  - AppComponent calls `onNavigationStart(targetUrl, currentUrl)` on every
 *    Router NavigationStart.
 *  - AppComponent calls `consumePendingScrollRestore()` on NavigationEnd to
 *    restore scroll for back-navigation.
 *  - SideMenu (menu-item) calls `clearFor(path)` when the user clicks a
 *    sidebar link, so a sidebar click always feels like a fresh entry.
 */
@Injectable({ providedIn: 'root' })
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    private cache = new Map<string, CacheEntry>();
    private scrollPositions = new Map<string, ScrollSnapshot>();
    private pendingRestoreKey: string | null = null;
    private lastWasRestore = false;
    private currentFullUrl = '';

    private static readonly POLICY_BY_NAME: Record<string, ReusePolicy> = {
        preserveAlways: preserveAlwaysPolicy,
        strict: strictPolicy,
        smart: smartPolicy,
    };

    onNavigationStart(targetUrl: string, currentUrl: string): void {
        this.captureScroll(currentUrl);
        this.lastWasRestore = false;
        this.currentFullUrl = currentUrl;

        const targetPath = stripQuery(targetUrl);
        const cached = this.cache.get(targetPath);

        console.groupCollapsed(`${LOG} NavigationStart`);
        console.log('from           :', currentUrl);
        console.log('to             :', targetUrl);
        console.log('targetPath     :', targetPath);
        console.log('cache size     :', this.cache.size);
        console.log('cache keys     :', Array.from(this.cache.keys()));
        if (cached) {
            console.log('cache HIT      :', targetPath);
            console.log('  cached.fullUrl :', cached.fullUrl);
            console.log('  cached.policy  :', cached.policy.name);
            console.log('  cached params  :', parseQueryParams(cached.fullUrl));
            console.log('  target params  :', parseQueryParams(targetUrl));
            const evict = cached.policy.shouldEvict(cached, targetUrl);
            console.log('  shouldEvict?   :', evict);
            if (evict) {
                this.cache.delete(targetPath);
                this.scrollPositions.delete(targetPath);
                console.warn(`${LOG} EVICTED by`, cached.policy.name, '→', targetPath);
            }
        } else {
            console.log('cache MISS     : (no entry for', targetPath + ')');
        }
        console.groupEnd();
    }

    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        const key = getRouteKey(route);
        const policy = this.resolvePolicy(route);
        const detach = policy !== null;
        console.log(
            `${LOG} shouldDetach   :`,
            detach,
            '| key =',
            key,
            '| policy =',
            policy?.name ?? '(none)',
            '| data.reuseRoute =',
            route.data?.['reuseRoute'],
        );
        return detach;
    }

    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        const key = getRouteKey(route);
        if (handle) {
            const policy = this.resolvePolicy(route);
            if (!policy) {
                console.warn(`${LOG} store SKIPPED  : no policy for key =`, key);
                return;
            }
            this.cache.set(key, {
                handle,
                fullUrl: this.currentFullUrl,
                policy,
            });
            console.log(
                `${LOG} STORED         :`,
                key,
                '| policy =',
                policy.name,
                '| fullUrl =',
                this.currentFullUrl,
            );
        } else {
            const had = this.cache.has(key);
            this.cache.delete(key);
            console.log(
                `${LOG} store NULL     :`,
                key,
                had ? '(deleted existing entry)' : '(no prior entry)',
            );
        }
    }

    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        // Symmetric with shouldDetach: only attach to routes that opted into
        // reuse via `data.reuseRoute`. Without this guard, a parent path-segment
        // route (e.g. the lazy-load wrapper above an empty-path child) shares a
        // cache key with its child, so both snapshots claim the same handle —
        // Angular then wires the view to two outlets and setRouterState recurses
        // until the stack overflows.
        const policy = this.resolvePolicy(route);
        if (!policy) return false;
        const key = getRouteKey(route);
        const has = this.cache.has(key);
        console.log(`${LOG} shouldAttach   :`, has, '| key =', key);
        return has;
    }

    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        // See shouldAttach — gate retrieval on the same policy check defensively.
        if (!this.resolvePolicy(route)) return null;
        const key = getRouteKey(route);
        const entry = this.cache.get(key);
        if (entry) {
            this.pendingRestoreKey = key;
            this.lastWasRestore = true;
            console.log(
                `${LOG} RETRIEVED      :`,
                key,
                '| scroll snap =',
                this.scrollPositions.get(key),
            );
        } else {
            console.log(`${LOG} retrieve MISS  :`, key);
        }
        return entry?.handle ?? null;
    }

    shouldReuseRoute(
        future: ActivatedRouteSnapshot,
        curr: ActivatedRouteSnapshot,
    ): boolean {
        const same = future.routeConfig === curr.routeConfig;
        // Only log when the answer is interesting (i.e. we're crossing routes).
        if (!same) {
            console.log(
                `${LOG} shouldReuseRoute: false | future =`,
                getRouteKey(future),
                '| curr =',
                getRouteKey(curr),
            );
        }
        return same;
    }

    isRestoringNavigation(): boolean {
        return this.lastWasRestore;
    }

    consumePendingScrollRestore(): ScrollSnapshot | null {
        if (!this.pendingRestoreKey) {
            console.log(`${LOG} consumeScroll  : (no pending key)`);
            return null;
        }
        const snap = this.scrollPositions.get(this.pendingRestoreKey) ?? null;
        console.log(
            `${LOG} consumeScroll  :`,
            this.pendingRestoreKey,
            '→',
            snap,
        );
        this.pendingRestoreKey = null;
        return snap;
    }

    clear(): void {
        console.warn(`${LOG} clear ALL      : (logout or reset)`);
        this.cache.clear();
        this.scrollPositions.clear();
        this.pendingRestoreKey = null;
        this.lastWasRestore = false;
    }

    clearFor(url: string): void {
        const path = stripQuery(url);
        const had = this.cache.has(path);
        this.cache.delete(path);
        this.scrollPositions.delete(path);
        console.log(
            `${LOG} clearFor       :`,
            path,
            had ? '(removed cache entry)' : '(no cache entry to remove)',
        );
    }

    private resolvePolicy(route: ActivatedRouteSnapshot): ReusePolicy | null {
        const flag = route.data?.['reuseRoute'];
        // Boolean `true` is the legacy/default flag and maps to preserveAlways
        // — the cache survives any nav and is only cleared by sidebar clicks
        // (clearFor) or logout (clear). Use a named string to opt into a
        // stricter rule (e.g. 'strict' or 'smart') for routes that need it.
        if (flag === true) return preserveAlwaysPolicy;
        if (typeof flag === 'string') {
            return CustomRouteReuseStrategy.POLICY_BY_NAME[flag] ?? null;
        }
        return null;
    }

    private captureScroll(url: string): void {
        const path = stripQuery(url);
        const snap = captureScrollSnapshot();
        if (snap.matSidenav || snap.outlet || snap.window) {
            this.scrollPositions.set(path, snap);
            console.log(`${LOG} captureScroll  :`, path, '→', snap);
        } else {
            console.log(`${LOG} captureScroll  :`, path, '(all zeros, skipped)');
        }
    }
}
